import {
  Inventory,
  ITEMS,
  MAX_STACK_SIZE
} from '../gameplay/inventory';
import type {
  ItemStack,
  InventorySnapshot
} from '../gameplay/inventory';
import type { PlayerStats } from '../gameplay/player';
import {
  RADIOACTIVE_RECYCLER_FUEL_PER_ORE,
  UPGRADES
} from '../gameplay/upgrades';
import type { ShipUpgrades } from '../gameplay/upgrades';

type GridTab = 'ship' | 'fps';
export type TabId = GridTab | 'recycler' | 'radio';
type TabMode = 'both' | 'fps-only';

/** Snapshot of the rescue state — fed in by GameDirector each time the
 *  inventory becomes visible so the radio panel can render the right
 *  button state without the overlay holding a direct reference to the
 *  rescue service. */
export interface RadioState {
  readonly canRequestRescue: boolean;
  readonly rescueActive: boolean;
  readonly rescueDescription: string;
}

export interface InventoryOverlayHandlers {
  /** Fires when the player clicks "Request Rescue" in the radio tab. */
  readonly onRequestRescue?: () => void;
  /** Returns the live radio state at render time — re-queried whenever
   *  the radio tab is shown or refreshed. */
  readonly getRadioState?: () => RadioState;
  /** Fires when the player switches to a tab inside the inventory.
   *  Used by the tutorial mission system to detect "Radio tab
   *  opened". Fires both on click AND on the initial activation when
   *  the overlay opens. */
  readonly onTabActivated?: (tab: TabId) => void;
}

interface HeldItem {
  readonly stack: ItemStack;
  readonly from: GridTab;
  readonly fromSlot: number;
}

const URANIUM_ITEM_ID = 'uranium-ore';

export class InventoryOverlay {
  readonly element: HTMLElement;

  private readonly tabButtons = new Map<TabId, HTMLButtonElement>();
  private readonly gridEl: HTMLElement;
  private readonly recyclerEl: HTMLElement;
  private readonly radioEl: HTMLElement;
  private readonly headerTitle: HTMLElement;
  private readonly capacityLabel: HTMLElement;
  private readonly heldEl: HTMLElement;
  private readonly heldIconEl: HTMLElement;
  private readonly heldCountEl: HTMLElement;
  private radioHandlers: InventoryOverlayHandlers = {};

  private readonly unsubscribes: Array<() => void> = [];
  private snapshots: Record<GridTab, InventorySnapshot> = {
    ship: [],
    fps: []
  };

  private activeTab: TabId = 'ship';
  private tabMode: TabMode = 'both';
  private held: HeldItem | null = null;
  private visible = false;
  private recyclerQty = 0;
  private readonly handleMouseMove: (ev: MouseEvent) => void;

  constructor(
    private readonly shipInventory: Inventory,
    private readonly fpsInventory: Inventory,
    private readonly stats: PlayerStats,
    private readonly shipUpgrades: ShipUpgrades
  ) {
    this.element = document.createElement('div');
    this.element.className = 'inventory-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'inventory-backdrop';

    const root = document.createElement('div');
    root.className = 'inventory-root';

    const header = document.createElement('header');
    header.className = 'inventory-header';
    this.headerTitle = document.createElement('div');
    this.headerTitle.className = 'inventory-title';
    this.headerTitle.textContent = 'INVENTORY';
    this.capacityLabel = document.createElement('div');
    this.capacityLabel.className = 'inventory-capacity';
    header.append(this.headerTitle, this.capacityLabel);

    const tabs = document.createElement('nav');
    tabs.className = 'inventory-tabs';

    const shipTab = this.createTab('ship', `Ship (${shipInventory.capacity})`);
    const fpsTab = this.createTab('fps', `Suit (${fpsInventory.capacity})`);
    const recyclerTab = this.createTab('recycler', 'Recycler');
    const radioTab = this.createTab('radio', 'Radio');
    tabs.append(shipTab, fpsTab, recyclerTab, radioTab);
    this.tabButtons.set('ship', shipTab);
    this.tabButtons.set('fps', fpsTab);
    this.tabButtons.set('recycler', recyclerTab);
    this.tabButtons.set('radio', radioTab);

    const panel = document.createElement('section');
    panel.className = 'inventory-panel';

    this.gridEl = document.createElement('div');
    this.gridEl.className = 'inventory-grid';
    this.gridEl.addEventListener('contextmenu', (ev) => ev.preventDefault());
    panel.appendChild(this.gridEl);

    this.recyclerEl = document.createElement('div');
    this.recyclerEl.className = 'recycler-panel';
    this.recyclerEl.style.display = 'none';
    panel.appendChild(this.recyclerEl);

    this.radioEl = document.createElement('div');
    this.radioEl.className = 'radio-panel';
    this.radioEl.style.display = 'none';
    panel.appendChild(this.radioEl);

    const tip = document.createElement('footer');
    tip.className = 'inventory-tip';
    tip.innerHTML =
      '<span><kbd>LMB</kbd> pick 1</span>' +
      '<span><kbd>RMB</kbd> pick all</span>' +
      '<span><kbd>I</kbd> / <kbd>ESC</kbd> close</span>';

    root.append(header, tabs, panel, tip);

    this.heldEl = document.createElement('div');
    this.heldEl.className = 'inventory-held';
    this.heldIconEl = document.createElement('div');
    this.heldIconEl.className = 'inventory-held-icon';
    this.heldCountEl = document.createElement('div');
    this.heldCountEl.className = 'inventory-held-count';
    this.heldEl.append(this.heldIconEl, this.heldCountEl);

    this.element.append(backdrop, root, this.heldEl);

    this.unsubscribes.push(
      shipInventory.subscribe((snap) => {
        this.snapshots.ship = snap;
        if (this.activeTab === 'ship') this.renderGrid();
        if (this.activeTab === 'recycler' && this.visible) this.renderRecycler();
      })
    );
    this.unsubscribes.push(
      fpsInventory.subscribe((snap) => {
        this.snapshots.fps = snap;
        if (this.activeTab === 'fps') this.renderGrid();
        // The recycler also pulls uranium from the suit, so its
        // available-ore line needs to reflect suit-side changes.
        if (this.activeTab === 'recycler' && this.visible) this.renderRecycler();
      })
    );
    this.unsubscribes.push(
      stats.subscribe(() => {
        if (this.activeTab === 'recycler' && this.visible) this.renderRecycler();
      })
    );
    this.unsubscribes.push(
      shipUpgrades.subscribe(() => {
        // If the player just installed the recycler while the overlay is
        // open, the new tab needs to appear immediately.
        if (this.visible) this.applyTabsVisibility();
      })
    );

    this.handleMouseMove = (ev: MouseEvent): void => {
      this.heldEl.style.transform = `translate(${ev.clientX - 24}px, ${ev.clientY - 24}px)`;
    };
  }

  isOpen(): boolean {
    return this.visible;
  }

  show(tabMode: TabMode, handlers: InventoryOverlayHandlers = {}): void {
    this.tabMode = tabMode;
    this.radioHandlers = handlers;
    if (tabMode === 'fps-only') {
      this.activeTab = 'fps';
    } else if (this.activeTab === 'recycler' && !this.canShowRecycler()) {
      this.activeTab = 'ship';
    } else if (this.activeTab === 'radio' && !this.canShowRadio()) {
      this.activeTab = 'ship';
    }
    this.applyTabsVisibility();
    this.applyActiveTab();
    // Surface the initial activation too — opening the inventory with
    // Radio remembered as the last tab should still register as a
    // tab-open event for any listener tracking it.
    this.radioHandlers.onTabActivated?.(this.activeTab);
    this.element.classList.add('is-visible');
    this.visible = true;
    window.addEventListener('mousemove', this.handleMouseMove);
  }

  hide(): void {
    if (this.held) {
      // Return held stack to its origin inventory (best effort).
      const inv = this.held.from === 'ship' ? this.shipInventory : this.fpsInventory;
      inv.addItem(this.held.stack.itemId, this.held.stack.count);
      this.held = null;
      this.renderHeld();
    }
    this.element.classList.remove('is-visible');
    this.visible = false;
    window.removeEventListener('mousemove', this.handleMouseMove);
  }

  dispose(): void {
    this.hide();
    for (const u of this.unsubscribes) u();
    this.element.remove();
  }

  private createTab(id: TabId, label: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'inventory-tab';
    btn.dataset['tab'] = id;
    btn.type = 'button';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      if (this.tabMode === 'fps-only' && id !== 'fps') return;
      if (id === 'recycler' && !this.canShowRecycler()) return;
      if (id === 'radio' && !this.canShowRadio()) return;
      const changed = this.activeTab !== id;
      this.activeTab = id;
      this.applyActiveTab();
      if (changed) this.radioHandlers.onTabActivated?.(id);
    });
    return btn;
  }

  private canShowRecycler(): boolean {
    return (
      this.tabMode !== 'fps-only' &&
      this.shipUpgrades.has('radioactive-recycler')
    );
  }

  /** Radio is a built-in piece of ship gear — always present when the
   *  inventory shows the ship tabs. (FPS-only mode hides it.) */
  private canShowRadio(): boolean {
    return this.tabMode !== 'fps-only';
  }

  private applyTabsVisibility(): void {
    const shipTab = this.tabButtons.get('ship')!;
    const recyclerTab = this.tabButtons.get('recycler')!;
    const radioTab = this.tabButtons.get('radio')!;
    shipTab.style.display = this.tabMode === 'fps-only' ? 'none' : '';
    recyclerTab.style.display = this.canShowRecycler() ? '' : 'none';
    radioTab.style.display = this.canShowRadio() ? '' : 'none';
  }

  private applyActiveTab(): void {
    for (const [id, btn] of this.tabButtons) {
      btn.classList.toggle('is-active', id === this.activeTab);
    }
    if (this.activeTab === 'recycler') {
      const recycler = UPGRADES['radioactive-recycler'];
      this.capacityLabel.textContent = `${recycler.name} · live conversion`;
      this.gridEl.style.display = 'none';
      this.recyclerEl.style.display = '';
      this.radioEl.style.display = 'none';
      this.recyclerQty = 0;
      this.renderRecycler();
    } else if (this.activeTab === 'radio') {
      this.capacityLabel.textContent = 'Subspace Comms · open channel';
      this.gridEl.style.display = 'none';
      this.recyclerEl.style.display = 'none';
      this.radioEl.style.display = '';
      this.renderRadio();
    } else {
      const inv = this.activeTab === 'ship' ? this.shipInventory : this.fpsInventory;
      this.capacityLabel.textContent = `${inv.name} · ${inv.capacity} slots`;
      this.recyclerEl.style.display = 'none';
      this.radioEl.style.display = 'none';
      this.gridEl.style.display = '';
      this.renderGrid();
    }
  }

  /** Render the radio panel — single channel for now ("Sector Recovery"
   *  with the rescue button). Stays generic so future channels (faction
   *  ops, system map alerts) can drop in as new option rows. */
  private renderRadio(): void {
    this.radioEl.innerHTML = '';
    const state = this.radioHandlers.getRadioState?.() ?? {
      canRequestRescue: false,
      rescueActive: false,
      rescueDescription: 'Run dry on fuel and a tug will be free of charge.'
    };

    const head = document.createElement('div');
    head.className = 'radio-head';
    head.innerHTML = `
      <div class="radio-icon" aria-hidden="true">${RADIO_ICON}</div>
      <div class="radio-head-info">
        <div class="radio-title">Sector Comms</div>
        <div class="radio-sub">Long-range channels available from the cockpit. Pick a transmission below.</div>
      </div>`;
    this.radioEl.appendChild(head);

    const list = document.createElement('div');
    list.className = 'radio-channels';

    const rescue = document.createElement('div');
    rescue.className = 'radio-channel';
    rescue.classList.toggle('is-active', state.rescueActive);

    const rescueBody = document.createElement('div');
    rescueBody.className = 'radio-channel-body';
    rescueBody.innerHTML = `
      <div class="radio-channel-name">Sector Recovery</div>
      <div class="radio-channel-blurb">${state.rescueDescription}</div>
    `;
    rescue.appendChild(rescueBody);

    const rescueBtn = document.createElement('button');
    rescueBtn.type = 'button';
    rescueBtn.className = 'radio-channel-btn';
    if (state.rescueActive) {
      rescueBtn.textContent = 'Tug en route…';
      rescueBtn.disabled = true;
    } else {
      rescueBtn.textContent = 'Request Rescue';
      rescueBtn.disabled = !state.canRequestRescue;
    }
    rescueBtn.addEventListener('click', () => {
      if (rescueBtn.disabled) return;
      this.radioHandlers.onRequestRescue?.();
      // Re-render to reflect the new state immediately.
      this.renderRadio();
    });
    rescue.appendChild(rescueBtn);

    list.appendChild(rescue);
    this.radioEl.appendChild(list);

    const note = document.createElement('div');
    note.className = 'radio-note';
    note.textContent = state.canRequestRescue
      ? 'Channel is open. Tug arrives at cruise speed — keep the comms tab open to hear it dock.'
      : state.rescueActive
        ? 'Tug already on its way. Stay put — the pilot will hail you on arrival.'
        : 'Channel locks until your tank reads 0. Burn it dry first.';
    this.radioEl.appendChild(note);
  }

  private currentInventory(): Inventory {
    return this.activeTab === 'ship' ? this.shipInventory : this.fpsInventory;
  }

  private renderGrid(): void {
    if (this.activeTab !== 'ship' && this.activeTab !== 'fps') return;
    const snap = this.snapshots[this.activeTab];
    const inv = this.currentInventory();

    if (this.gridEl.childElementCount !== inv.capacity) {
      this.gridEl.innerHTML = '';
      for (let i = 0; i < inv.capacity; i++) {
        const slot = document.createElement('button');
        slot.className = 'inv-slot';
        slot.type = 'button';
        slot.dataset['index'] = String(i);
        slot.addEventListener('mousedown', (ev) =>
          this.handleSlotMouseDown(ev, i)
        );
        slot.addEventListener('contextmenu', (ev) => ev.preventDefault());
        this.gridEl.appendChild(slot);
      }
    }

    for (let i = 0; i < inv.capacity; i++) {
      const slot = this.gridEl.children[i] as HTMLElement;
      const stack = snap[i] ?? null;
      this.renderSlot(slot, stack);
    }
  }

  private renderSlot(slot: HTMLElement, stack: ItemStack | null): void {
    slot.innerHTML = '';
    slot.classList.toggle('is-filled', !!stack);
    slot.style.removeProperty('--slot-accent');
    slot.style.removeProperty('--slot-glow');
    if (!stack) return;
    const def = ITEMS[stack.itemId];
    slot.style.setProperty('--slot-accent', def.accent);
    slot.style.setProperty('--slot-glow', def.accentGlow);

    const icon = document.createElement('div');
    icon.className = 'inv-icon';
    icon.innerHTML = def.icon;

    const count = document.createElement('span');
    count.className = 'inv-count';
    count.textContent = String(stack.count);

    slot.append(icon, count);
    slot.title = def.name;
  }

  private renderHeld(): void {
    if (!this.held) {
      this.heldEl.classList.remove('is-visible');
      this.heldIconEl.innerHTML = '';
      this.heldCountEl.textContent = '';
      return;
    }
    const def = ITEMS[this.held.stack.itemId];
    this.heldEl.classList.add('is-visible');
    this.heldEl.style.setProperty('--slot-accent', def.accent);
    this.heldEl.style.setProperty('--slot-glow', def.accentGlow);
    this.heldIconEl.innerHTML = def.icon;
    this.heldCountEl.textContent = String(this.held.stack.count);
  }

  private renderRecycler(): void {
    const recycler = UPGRADES['radioactive-recycler'];
    const shipOre = this.countOreInInventory(this.shipInventory);
    const suitOre = this.countOreInInventory(this.fpsInventory);
    const oreCount = shipOre + suitOre;
    const fuel = this.stats.fuel;
    const fuelMax = this.stats.fuelMax;
    const fuelMissing = Math.max(0, fuelMax - fuel);
    const oreNeededForFull = Math.ceil(
      fuelMissing / RADIOACTIVE_RECYCLER_FUEL_PER_ORE
    );
    const maxConvertable = Math.min(oreCount, oreNeededForFull);
    if (this.recyclerQty > maxConvertable) {
      this.recyclerQty = maxConvertable;
    }
    const qty = this.recyclerQty;
    const fuelPreview = Math.min(
      fuelMissing,
      qty * RADIOACTIVE_RECYCLER_FUEL_PER_ORE
    );
    const fuelAfter = fuel + fuelPreview;
    const fuelPct = (fuel / fuelMax) * 100;
    const previewPct = (fuelPreview / fuelMax) * 100;

    this.recyclerEl.innerHTML = '';
    this.recyclerEl.style.setProperty('--recycler-accent', recycler.accent);
    this.recyclerEl.style.setProperty('--recycler-glow', recycler.accentGlow);

    const head = document.createElement('div');
    head.className = 'recycler-head';
    head.innerHTML = `
      <div class="recycler-icon">${recycler.icon}</div>
      <div class="recycler-head-info">
        <div class="recycler-title">${recycler.name}</div>
        <div class="recycler-sub">${recycler.description}</div>
        <div class="recycler-rate">
          1 × <span class="recycler-rate-ore">Uranium ore</span>
          → ${RADIOACTIVE_RECYCLER_FUEL_PER_ORE}
          <span class="recycler-rate-fuel">fuel</span>
        </div>
      </div>`;
    this.recyclerEl.appendChild(head);

    // Show ship + suit breakdowns when the player has ore in both, so it
    // reads as "we're pulling from your whole stash" rather than the
    // ship-only number some players are used to.
    const oreLineExtra =
      suitOre > 0
        ? `<span class="recycler-stat-split">ship ${shipOre} · suit ${suitOre}</span>`
        : '';
    const stats = document.createElement('div');
    stats.className = 'recycler-stats';
    stats.innerHTML = `
      <div class="recycler-stat">
        <span class="recycler-stat-label">Uranium available</span>
        <span class="recycler-stat-value">${oreCount}</span>
        ${oreLineExtra}
      </div>
      <div class="recycler-stat">
        <span class="recycler-stat-label">Fuel</span>
        <span class="recycler-stat-value">${Math.round(fuel)} / ${fuelMax}</span>
      </div>`;
    this.recyclerEl.appendChild(stats);

    const fuelBar = document.createElement('div');
    fuelBar.className = 'recycler-bar';
    fuelBar.innerHTML = `
      <div class="recycler-bar-track">
        <div class="recycler-bar-fill" style="width:${fuelPct}%"></div>
        <div class="recycler-bar-preview" style="left:${fuelPct}%; width:${previewPct}%"></div>
      </div>
      <div class="recycler-bar-meta">
        <span>After: ${Math.round(fuelAfter)} / ${fuelMax}</span>
        <span class="recycler-bar-delta">+${Math.round(fuelPreview)} fuel</span>
      </div>`;
    this.recyclerEl.appendChild(fuelBar);

    const controls = document.createElement('div');
    controls.className = 'recycler-controls';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'recycler-slider';
    slider.min = '0';
    slider.max = String(maxConvertable);
    slider.value = String(qty);
    slider.disabled = maxConvertable <= 0;
    slider.addEventListener('input', () => {
      this.recyclerQty = Number(slider.value);
      this.renderRecycler();
    });
    controls.appendChild(slider);

    const qtyRow = document.createElement('div');
    qtyRow.className = 'recycler-qty-row';
    qtyRow.innerHTML = `
      <button type="button" class="recycler-step" data-step="-1">−</button>
      <div class="recycler-qty">
        <span class="recycler-qty-num">${qty}</span>
        <span class="recycler-qty-unit">/ ${maxConvertable} ore</span>
      </div>
      <button type="button" class="recycler-step" data-step="+1">+</button>`;
    controls.appendChild(qtyRow);

    qtyRow.querySelectorAll<HTMLButtonElement>('.recycler-step').forEach((btn) => {
      btn.disabled = maxConvertable <= 0;
      btn.addEventListener('click', () => {
        const step = btn.dataset['step'] === '+1' ? 1 : -1;
        this.recyclerQty = Math.max(
          0,
          Math.min(maxConvertable, this.recyclerQty + step)
        );
        this.renderRecycler();
      });
    });

    const actionRow = document.createElement('div');
    actionRow.className = 'recycler-actions';

    const halfBtn = document.createElement('button');
    halfBtn.type = 'button';
    halfBtn.className = 'recycler-quick';
    halfBtn.textContent = 'Half';
    halfBtn.disabled = maxConvertable <= 0;
    halfBtn.addEventListener('click', () => {
      this.recyclerQty = Math.floor(maxConvertable / 2);
      this.renderRecycler();
    });

    const maxBtn = document.createElement('button');
    maxBtn.type = 'button';
    maxBtn.className = 'recycler-quick';
    maxBtn.textContent = 'Max';
    maxBtn.disabled = maxConvertable <= 0;
    maxBtn.addEventListener('click', () => {
      this.recyclerQty = maxConvertable;
      this.renderRecycler();
    });

    const recycleBtn = document.createElement('button');
    recycleBtn.type = 'button';
    recycleBtn.className = 'recycler-confirm';
    recycleBtn.disabled = qty <= 0;
    recycleBtn.textContent =
      qty > 0
        ? `Recycle ${qty} ore · +${Math.round(fuelPreview)} fuel`
        : 'Recycle';
    recycleBtn.addEventListener('click', () => {
      this.performRecycle();
    });

    actionRow.append(halfBtn, maxBtn, recycleBtn);
    controls.appendChild(actionRow);

    this.recyclerEl.appendChild(controls);

    if (oreCount === 0) {
      const empty = document.createElement('div');
      empty.className = 'recycler-empty';
      empty.textContent =
        'No uranium ore in the ship or suit inventory. Mine green-glowing asteroids to gather some.';
      this.recyclerEl.appendChild(empty);
    } else if (fuelMissing <= 0) {
      const empty = document.createElement('div');
      empty.className = 'recycler-empty';
      empty.textContent = 'Fuel tank already full.';
      this.recyclerEl.appendChild(empty);
    }
  }

  private countOreInInventory(inv: Inventory): number {
    let total = 0;
    const snap = inv.snapshot();
    for (const slot of snap) {
      if (slot && slot.itemId === URANIUM_ITEM_ID) total += slot.count;
    }
    return total;
  }

  private performRecycle(): void {
    const qty = this.recyclerQty;
    if (qty <= 0) return;
    const drained = this.drainOre(qty);
    if (drained <= 0) return;
    const fuelAdded = drained * RADIOACTIVE_RECYCLER_FUEL_PER_ORE;
    this.stats.addFuel(fuelAdded);
    this.recyclerQty = 0;
    this.renderRecycler();
  }

  private drainOre(requested: number): number {
    // Pull from the ship first, then the suit. The ship slots are bigger
    // (20 slots vs the suit's 6), so prefer-ship-first keeps the player's
    // small suit stash intact for as long as it can — they're more
    // likely to want to spend cargo before suit stash on a refuel.
    let drained = this.drainFromInventory(this.shipInventory, requested);
    if (drained < requested) {
      drained += this.drainFromInventory(this.fpsInventory, requested - drained);
    }
    return drained;
  }

  private drainFromInventory(inv: Inventory, requested: number): number {
    if (requested <= 0) return 0;
    let drained = 0;
    const snap = inv.snapshot();
    for (let i = 0; i < snap.length && drained < requested; i++) {
      const slot = snap[i];
      if (!slot || slot.itemId !== URANIUM_ITEM_ID) continue;
      const take = Math.min(slot.count, requested - drained);
      const taken = inv.takeFromSlot(i, take);
      if (taken) drained += taken.count;
    }
    return drained;
  }

  private handleSlotMouseDown(ev: MouseEvent, slotIndex: number): void {
    if (this.activeTab === 'recycler') return;
    if (ev.button !== 0 && ev.button !== 2) return;
    ev.preventDefault();
    const isRight = ev.button === 2;

    const inv = this.currentInventory();
    const tab = this.activeTab as GridTab;
    const slotSnapshot = this.snapshots[tab][slotIndex] ?? null;

    if (!this.held) {
      // Pick up from slot.
      if (!slotSnapshot) return;
      const take = isRight ? slotSnapshot.count : 1;
      const taken = inv.takeFromSlot(slotIndex, take);
      if (!taken) return;
      this.held = {
        stack: taken,
        from: tab,
        fromSlot: slotIndex
      };
      this.renderHeld();
      return;
    }

    // Holding an item: deposit into slot.
    const heldDef = this.held.stack.itemId;
    const slotDef = slotSnapshot?.itemId ?? null;

    if (slotDef === null || slotDef === heldDef) {
      const canFit =
        slotDef === null
          ? MAX_STACK_SIZE
          : MAX_STACK_SIZE - (slotSnapshot?.count ?? 0);
      const depositCount = isRight
        ? Math.min(this.held.stack.count, canFit)
        : Math.min(1, canFit);
      if (depositCount <= 0) return;

      const deposit: ItemStack = {
        itemId: this.held.stack.itemId,
        count: depositCount
      };
      inv.depositToSlot(slotIndex, deposit);
      const remainingHeld = this.held.stack.count - depositCount;
      if (remainingHeld <= 0) {
        this.held = null;
      } else {
        this.held = {
          ...this.held,
          stack: { itemId: this.held.stack.itemId, count: remainingHeld }
        };
      }
      this.renderHeld();
      return;
    }

    // Slot has a different item: swap if holding the full stack, otherwise
    // return the held to origin and pick up the slot's content instead.
    const swapped = inv.swapSlot(slotIndex, this.held.stack);
    if (swapped) {
      this.held = {
        stack: swapped,
        from: tab,
        fromSlot: slotIndex
      };
    } else {
      this.held = null;
    }
    this.renderHeld();
  }
}

const RADIO_ICON = `
<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="3" y="13" width="26" height="14" rx="2.5" stroke="currentColor" stroke-width="2" />
  <circle cx="22.5" cy="20" r="3.2" stroke="currentColor" stroke-width="2" />
  <path d="M9 19h6M9 22.5h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <path d="M11 13l5-7 5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
  <circle cx="22.5" cy="20" r="0.9" fill="currentColor" />
</svg>`;

